import { FC, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useSubmitQuizMutation, teachurKeys, type Quiz } from '@teachur/http-client';
import { useQueryClient } from '@tanstack/react-query';
import { useSingleCourse } from '@new/features/courses/contexts/SingleCourseProvider';
import { useExamContext } from '@new/features/courses/contexts/ExamProvider';
import { useRequireSubscribed } from '@new/features/subscription/hooks/useRequireSubscribed';
import { Button } from '@new/common/ui';

const FIRST_QUESTION_INDEX = 0;
const LAST_QUESTION_OFFSET = 1;

interface ExamQuestionsFooterProps {
  quiz: Quiz;
  userAnswers: Record<number, number>;
  currentQuestionIndex: number;
  setCurrentQuestionIndex: (index: number) => void;
}

export const ExamQuestionsFooter: FC<ExamQuestionsFooterProps> = ({
  quiz,
  userAnswers,
  currentQuestionIndex,
  setCurrentQuestionIndex,
}) => {
  const { course } = useSingleCourse();
  const { setExamMode } = useExamContext();
  const queryClient = useQueryClient();
  const requireSubscribed = useRequireSubscribed();

  const submitQuizMutation = useSubmitQuizMutation({
    onSuccess: (data) => {
      setExamMode({ type: 'loading', attempt: data });
      queryClient.invalidateQueries({ queryKey: teachurKeys.quizzes.list(course?.id || 0) });
      queryClient.invalidateQueries({ queryKey: teachurKeys.courses.completion(course?.id || 0) });
      queryClient.invalidateQueries({ queryKey: teachurKeys.courses.detail(course?.id || 0) });
    },
  });

  const totalQuestions = quiz.assessments?.length ?? 0;
  const isFirstQuestion = currentQuestionIndex === FIRST_QUESTION_INDEX;
  const isLastQuestion = currentQuestionIndex === totalQuestions - LAST_QUESTION_OFFSET;
  const isLoading = submitQuizMutation.isPending;

  const currentAssessment = quiz.assessments?.[currentQuestionIndex];
  const isCurrentQuestionAnswered =
    currentAssessment != null && userAnswers[currentAssessment.id] !== undefined;

  const canSubmit = isLastQuestion && isCurrentQuestionAnswered;

  const handleSubmit = useCallback(async () => {
    try {
      await submitQuizMutation.mutateAsync({
        quizId: quiz.id,
        data: {
          userAnswers,
          isSubmit: true,
        },
      });
    } catch {
      toast.error('Failed to submit quiz');
    }
  }, [quiz.id, userAnswers, submitQuizMutation, setExamMode]);

  const onNext = () => {
    setCurrentQuestionIndex(Math.min(currentQuestionIndex + 1, totalQuestions - 1));
  };

  const onPrev = () => {
    setCurrentQuestionIndex(Math.max(currentQuestionIndex - 1, FIRST_QUESTION_INDEX));
  };

  return (
    <div className="flex items-center justify-between">
      <Button variant="neutral" onClick={onPrev} disabled={isFirstQuestion || isLoading}>
        Back
      </Button>

      <Button
        variant="primary"
        onClick={isLastQuestion ? requireSubscribed(handleSubmit) : onNext}
        disabled={
          isLoading ||
          (!isLastQuestion && !isCurrentQuestionAnswered) ||
          (isLastQuestion && !canSubmit)
        }
      >
        {isLoading ? 'Submitting...' : isLastQuestion ? 'Submit' : 'Next'}
      </Button>
    </div>
  );
};
